// File: /var/www/app2/frontend/src/components/ReportDetail.tsx

import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { User } from "../App";
import "../styles/ReportDetail.css";

// Image metadata interface for pictures with descriptions
interface ImageMetadata {
  path: string;
  description: string;
}

// Uploading picture interface with preview
interface UploadingPicture {
  file: File;
  description: string;
  preview: string;
}

// Report data interface for the main report content
interface ReportData {
  heardGospel: number | string;
  receivedJesus: number | string;
  baptized: number | string;
  discoveryBibleStudyStarted: number | string;
  churchesPlanted: number | string;
  leadersTrained: number | string;
  testimony: string;
  challenges: string;
  prayerRequests: string;
  pictures: ImageMetadata[];
  // Source document the India team worked from — used to check data accuracy
  // and translation. Absent on reports submitted before 2026-09.
  originalReport?: {
    path: string;
    originalName: string;
    mimetype?: string;
    size?: number;
    uploadedAt?: string;
  } | null;
}

// Edit data interface extending ReportData
interface EditData extends ReportData {}

// Note interface for displaying notes
interface Note {
  id: number;
  note_text: string;
  author_name: string;
  created_at: string;
}

// Rejection reason interface
interface RejectionReason {
  reason: string;
  rejected_by_name: string;
  created_at: string;
}

// Report interface for the full report object
interface Report {
  id: number;
  missionary_name: string;
  missionary_number: string;
  month: number;
  year: number;
  state: string;
  location: string;
  status: string;
  submission_date: string;
  approved_date: string | null;
  rejected_date: string | null;
  data: ReportData;
}

// Component props interface
interface ReportDetailProps {
  user: User;
}

// Route params interface
interface ReportParams extends Record<string, string | undefined> {
  id?: string;
}

function ReportDetail({ user }: ReportDetailProps): React.ReactElement {
  const { id } = useParams<ReportParams>();
  const navigate = useNavigate();
  const [report, setReport] = useState<Report | null>(null);
  const [notes, setNotes] = useState<Note[]>([]);
  const [rejectionReasons, setRejectionReasons] = useState<RejectionReason[]>([]);
  const [newNote, setNewNote] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");

  // Edit mode states
  const [editMode, setEditMode] = useState<boolean>(false);
  const [editData, setEditData] = useState<EditData | null>(null);
  const [editMonth, setEditMonth] = useState<number>(0);
  const [editYear, setEditYear] = useState<number>(0);
  const [saving, setSaving] = useState<boolean>(false);
  const [uploadingPictures, setUploadingPictures] = useState<UploadingPicture[]>([]);

  useEffect(() => {
    fetchReportDetail();
  }, [id]);

  // Scroll to top when viewing a new report
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const fetchReportDetail = async (): Promise<void> => {
    try {
      const response = await axios.get(`/api/reports/${id}`);
      setReport(response.data.report);
      setNotes(response.data.notes || []);
      setRejectionReasons(response.data.rejection_reasons || []);
    } catch (error: any) {
      console.error("Failed to fetch report:", error);
      setError("Failed to load report details");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus: string): Promise<void> => {
    const reason: string | null =
      newStatus === "rejected"
        ? prompt("Please provide a reason for rejection:")
        : null;

    if (newStatus === "rejected" && !reason) {
      return; // User cancelled
    }

    if (!window.confirm(`Are you sure you want to ${newStatus} this report?`)) {
      return;
    }

    try {
      await axios.patch(`/api/reports/${id}/status`, {
        status: newStatus,
        reason: reason || "",
      });

      // Refresh report data
      fetchReportDetail();
      alert(`Report ${newStatus} successfully!`);
    } catch (error: any) {
      alert(
        "Failed to update status: " +
          (error.response?.data?.error || error.message)
      );
    }
  };

  const handleRevertToReview = async (): Promise<void> => {
    if (
      !window.confirm(
        "Are you sure you want to change this report back to Review status?"
      )
    ) {
      return;
    }

    try {
      await axios.patch(`/api/reports/${id}/status`, {
        status: "review",
        reason: "Reverted to review for corrections",
      });

      fetchReportDetail();
      alert("Report status changed to Review successfully!");
    } catch (error: any) {
      alert(
        "Failed to update status: " +
          (error.response?.data?.error || error.message)
      );
    }
  };

  const handleAddNote = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!newNote.trim()) return;

    try {
      await axios.post(`/api/reports/${id}/notes`, {
        note: newNote,
      });

      setNewNote("");
      fetchReportDetail(); // Refresh to show new note
    } catch (error: any) {
      alert(
        "Failed to add note: " + (error.response?.data?.error || error.message)
      );
    }
  };

  const handleEditToggle = (): void => {
    if (!editMode) {
      // Enter edit mode
      if (report) {
        setEditData({
          ...report.data,
          pictures: processPictures(report.data),
        });
        setEditMonth(report.month);
        setEditYear(report.year);
        setEditMode(true);
      }
    } else {
      // Cancel edit mode
      if (window.confirm("Discard all changes?")) {
        setEditMode(false);
        setEditData(null);
        setEditMonth(0);
        setEditYear(0);
        setUploadingPictures([]);
      }
    }
  };

  const handleEditChange = (field: string, value: any): void => {
    if (editData) {
      setEditData({
        ...editData,
        [field]: value,
      });
    }
  };

  const handleRemovePicture = (index: number): void => {
    if (editData) {
      const newPictures = editData.pictures.filter((_, i) => i !== index);
      setEditData({
        ...editData,
        pictures: newPictures,
      });
    }
  };

  const handleAddNewPicture = (): void => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = async (e: Event): Promise<void> => {
      const target = e.target as HTMLInputElement;
      const file = target.files?.[0];
      if (file && editData) {
        const description = prompt("Enter description for this picture:");
        if (description !== null) {
          setUploadingPictures([
            ...uploadingPictures,
            {
              file: file,
              description:
                description ||
                `Picture ${
                  editData.pictures.length + uploadingPictures.length + 1
                }`,
              preview: URL.createObjectURL(file),
            },
          ]);
        }
      }
    };
    input.click();
  };

  const handleRemoveUploadingPicture = (index: number): void => {
    setUploadingPictures(uploadingPictures.filter((_, i) => i !== index));
  };

  const handleUpdatePictureDescription = (
    index: number,
    isNew: boolean,
    newDescription: string
  ): void => {
    if (isNew) {
      const newUploading = [...uploadingPictures];
      newUploading[index].description = newDescription;
      setUploadingPictures(newUploading);
    } else {
      if (editData) {
        const newPictures = [...editData.pictures];
        newPictures[index].description = newDescription;
        setEditData({
          ...editData,
          pictures: newPictures,
        });
      }
    }
  };

  const handleSaveChanges = async (): Promise<void> => {
    if (!window.confirm("Save all changes to this report?")) {
      return;
    }

    setSaving(true);
    try {
      // Prepare the update data
      if (!editData) return;

      const updateData: any = {
        ...editData,
        // Ensure numeric fields are properly formatted
        heardGospel: parseInt(String(editData.heardGospel)) || 0,
        receivedJesus: parseInt(String(editData.receivedJesus)) || 0,
        baptized: parseInt(String(editData.baptized)) || 0,
        discoveryBibleStudyStarted:
          parseInt(String(editData.discoveryBibleStudyStarted)) || 0,
        churchesPlanted: parseInt(String(editData.churchesPlanted)) || 0,
        leadersTrained: parseInt(String(editData.leadersTrained)) || 0,
        // Include month and year if changed
        month: editMonth,
        year: editYear,
      };

      // Handle new picture uploads if any
      if (uploadingPictures.length > 0) {
        // This would require implementing a picture upload endpoint
        // For now, we'll just add them to the pictures array with a placeholder
        const newPictures = uploadingPictures.map((pic, index) => ({
          path: `/uploads/pending-${Date.now()}-${index}.jpg`,
          description: pic.description,
        }));
        updateData.pictures = [...updateData.pictures, ...newPictures];
      }

      // Send update request
      await axios.put(`/api/reports/${id}/update`, updateData);

      alert("Report updated successfully!");
      setEditMode(false);
      setEditData(null);
      setEditMonth(0);
      setEditYear(0);
      setUploadingPictures([]);
      fetchReportDetail(); // Refresh the data
    } catch (error: any) {
      alert(
        "Failed to save changes: " +
          (error.response?.data?.error || error.message)
      );
    } finally {
      setSaving(false);
    }
  };

  const getStatusBadge = (status: string): React.ReactElement => {
    return (
      <span className={`status-badge ${status}`}>{status.toUpperCase()}</span>
    );
  };

  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString();
  };

  const months: string[] = [
    "",
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  // Helper function to process pictures - handles both new and legacy formats
  const processPictures = (reportData: ReportData): ImageMetadata[] => {
    if (!reportData.pictures) return [];

    if (reportData.pictures.length > 0) {
      if (
        typeof reportData.pictures[0] === "object" &&
        (reportData.pictures[0] as any).path
      ) {
        return reportData.pictures;
      } else if (typeof reportData.pictures[0] === "string") {
        return (reportData.pictures as any).map((path: string, index: number) => ({
          path: path,
          description: `Picture ${index + 1}`,
        }));
      }
    }

    return [];
  };

  // Note: HEIC/HEIF images are now converted to JPEG server-side during upload
  // No client-side conversion needed anymore!

  if (loading) {
    return <div className="loading">Loading report details...</div>;
  }

  if (error || !report) {
    return (
      <div className="report-detail-container">
        <h2>Error</h2>
        <div className="report-section">
          <p>{error || "Report not found"}</p>
          <button onClick={() => navigate("/reports")} className="back-btn">
            Back to Reports
          </button>
        </div>
      </div>
    );
  }

  const reportData = report.data || {};
  const pictures = editMode ? editData?.pictures : processPictures(reportData);

  const isAdmin = user.role === "admin" || user.role === "superadmin";
  const isSupporter = user.role === "supporter";
  const canEdit =
    isAdmin && (report.status === "review" || report.status === "rejected"); // Admin can edit review and rejected
  const canRevert = isAdmin && (report.status === "approved" || report.status === "action_required" || report.status === "rejected");

  return (
    <div className="report-detail-container">
      <div className="report-header">
        <h2>
          Report Details{" "}
          {editMode && <span style={{ color: "#f56565" }}>(Edit Mode)</span>}
        </h2>
        <div style={{ display: "flex", gap: "10px" }}>
          {canEdit && !editMode && (
            <button onClick={handleEditToggle} className="edit-btn">
              Edit Report
            </button>
          )}
          {editMode && (
            <>
              <button
                onClick={handleSaveChanges}
                className="save-btn"
                disabled={saving}
              >
                {saving ? "Saving..." : "Save Changes"}
              </button>
              <button onClick={handleEditToggle} className="cancel-btn">
                Cancel
              </button>
            </>
          )}
          <button onClick={() => navigate("/reports")} className="back-btn">
            ← Back to Reports
          </button>
        </div>
      </div>

      {/* Report Header */}
      <div className="report-section">
        <h3>Report Information</h3>
        <div className="report-field">
          <label>Missionary:</label>
          <span>
            {report.missionary_name} ({report.missionary_number})
          </span>
        </div>
        <div className="report-field">
          <label>Period:</label>
          {editMode ? (
            <span className="period-edit">
              <select
                value={editMonth}
                onChange={(e) => setEditMonth(parseInt(e.target.value))}
                className="period-select"
              >
                {months.slice(1).map((monthName, index) => (
                  <option key={index + 1} value={index + 1}>
                    {monthName}
                  </option>
                ))}
              </select>
              <select
                value={editYear}
                onChange={(e) => setEditYear(parseInt(e.target.value))}
                className="period-select"
              >
                {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 5 + i).map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </span>
          ) : (
            <span>
              {months[report.month]} {report.year}
            </span>
          )}
        </div>
        <div className="report-field">
          <label>Location:</label>
          <span>
            {report.state}, {report.location}
          </span>
        </div>
        <div className="report-field">
          <label>Status:</label>
          <span>{getStatusBadge(report.status)}</span>
        </div>
        <div className="report-field">
          <label>Submitted:</label>
          <span>{formatDate(report.submission_date)}</span>
        </div>
        {report.approved_date && (
          <div className="report-field">
            <label>Approved:</label>
            <span>{formatDate(report.approved_date)}</span>
          </div>
        )}
        {report.rejected_date && (
          <div className="report-field">
            <label>Rejected:</label>
            <span>{formatDate(report.rejected_date)}</span>
          </div>
        )}
        {rejectionReasons.length > 0 && (
          <div className="report-field">
            <label>Rejection Reason:</label>
            <span>
              {rejectionReasons.map((rr, index) => (
                <div key={index} style={{ marginBottom: index < rejectionReasons.length - 1 ? "8px" : "0" }}>
                  <span>{rr.reason}</span>
                  <span style={{ color: "#888", fontSize: "0.85em", marginLeft: "8px" }}>
                    — {rr.rejected_by_name || "Unknown"}, {formatDate(rr.created_at)}
                  </span>
                </div>
              ))}
            </span>
          </div>
        )}
      </div>

      {/* Ministry Statistics */}
      <div className="report-section">
        <h3>Ministry Statistics</h3>
        <div className="metrics-grid">
          <div className="metric-item">
            <div className="metric-label">People Who Heard Gospel</div>
            {editMode ? (
              <input
                type="number"
                className="metric-input"
                value={editData?.heardGospel || 0}
                onChange={(e) =>
                  handleEditChange("heardGospel", e.target.value)
                }
                min="0"
              />
            ) : (
              <div className="metric-value">{reportData.heardGospel || 0}</div>
            )}
          </div>
          <div className="metric-item">
            <div className="metric-label">People Who Received Jesus</div>
            {editMode ? (
              <input
                type="number"
                className="metric-input"
                value={editData?.receivedJesus || 0}
                onChange={(e) =>
                  handleEditChange("receivedJesus", e.target.value)
                }
                min="0"
              />
            ) : (
              <div className="metric-value">
                {reportData.receivedJesus || 0}
              </div>
            )}
          </div>
          <div className="metric-item">
            <div className="metric-label">People Baptized</div>
            {editMode ? (
              <input
                type="number"
                className="metric-input"
                value={editData?.baptized || 0}
                onChange={(e) => handleEditChange("baptized", e.target.value)}
                min="0"
              />
            ) : (
              <div className="metric-value">{reportData.baptized || 0}</div>
            )}
          </div>
          <div className="metric-item">
            <div className="metric-label">Discovery Bible Studies Started</div>
            {editMode ? (
              <input
                type="number"
                className="metric-input"
                value={editData?.discoveryBibleStudyStarted || 0}
                onChange={(e) =>
                  handleEditChange("discoveryBibleStudyStarted", e.target.value)
                }
                min="0"
              />
            ) : (
              <div className="metric-value">
                {reportData.discoveryBibleStudyStarted || 0}
              </div>
            )}
          </div>
          <div className="metric-item">
            <div className="metric-label">Churches Planted</div>
            {editMode ? (
              <input
                type="number"
                className="metric-input"
                value={editData?.churchesPlanted || 0}
                onChange={(e) =>
                  handleEditChange("churchesPlanted", e.target.value)
                }
                min="0"
              />
            ) : (
              <div className="metric-value">
                {reportData.churchesPlanted || 0}
              </div>
            )}
          </div>
          <div className="metric-item">
            <div className="metric-label">Leaders Trained</div>
            {editMode ? (
              <input
                type="number"
                className="metric-input"
                value={editData?.leadersTrained || 0}
                onChange={(e) =>
                  handleEditChange("leadersTrained", e.target.value)
                }
                min="0"
              />
            ) : (
              <div className="metric-value">
                {reportData.leadersTrained || 0}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Narrative Sections */}
      <div className="report-section">
        <h3>Testimony</h3>
        {editMode ? (
          <textarea
            className="narrative-textarea"
            value={editData?.testimony || ""}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleEditChange("testimony", e.target.value)}
            rows={6}
          />
        ) : (
          <div className="narrative-content">
            {reportData.testimony || "No testimony provided"}
          </div>
        )}
      </div>

      <div className="report-section">
        <h3>Challenges</h3>
        {editMode ? (
          <textarea
            className="narrative-textarea"
            value={editData?.challenges || ""}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleEditChange("challenges", e.target.value)}
            rows={6}
          />
        ) : (
          <div className="narrative-content">
            {reportData.challenges || "No challenges reported"}
          </div>
        )}
      </div>

      <div className="report-section">
        <h3>Prayer Requests</h3>
        {editMode ? (
          <textarea
            className="narrative-textarea"
            value={editData?.prayerRequests || ""}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleEditChange("prayerRequests", e.target.value)}
            rows={6}
          />
        ) : (
          <div className="narrative-content">
            {reportData.prayerRequests || "No prayer requests"}
          </div>
        )}
      </div>

      {/* Original Missionary Report — the source document, admin-only */}
      {isAdmin && (
        <div className="report-section">
          <h3>Original Missionary Report</h3>
          {reportData.originalReport ? (
            (() => {
              const doc = reportData.originalReport;
              const fileName = doc.path.replace("/uploads/", "");
              const href = `/api/document-proxy/${fileName}?name=${encodeURIComponent(doc.originalName || fileName)}`;
              const sizeKb = doc.size ? Math.max(1, Math.round(doc.size / 1024)) : null;
              return (
                <div>
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontWeight: "bold" }}
                  >
                    {doc.originalName || fileName}
                  </a>
                  {sizeKb !== null && (
                    <span style={{ color: "#666", marginLeft: "8px" }}>({sizeKb} KB)</span>
                  )}
                  <div style={{ fontSize: "13px", color: "#666", marginTop: "6px" }}>
                    Opens in a new tab. Word documents will download.
                  </div>
                </div>
              );
            })()
          ) : (
            <div style={{ color: "#666" }}>
              No original report was attached to this submission.
            </div>
          )}
        </div>
      )}

      {/* Pictures with Descriptions */}
      {(pictures && pictures.length > 0) || editMode ? (
        <div className="report-section">
          <h3>
            Pictures ({pictures ? pictures.length : 0} + {uploadingPictures.length})
            {editMode && (
              <button
                onClick={handleAddNewPicture}
                className="add-picture-btn"
                style={{ marginLeft: "10px" }}
              >
                + Add Picture
              </button>
            )}
          </h3>
          <div className="pictures-grid-with-descriptions">
            {pictures && pictures.map((pic, index) => {
              const imagePath = pic.path.replace("/uploads/", "");
              return (
                <div key={index} className="picture-card">
                  <div className="picture-container">
                    <img
                      src={`/api/image-proxy/${imagePath}`}
                      alt={pic.description || `Report picture ${index + 1}`}
                      onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                        const img = e.target as HTMLImageElement;
                        img.src =
                          "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2RkZCIvPjx0ZXh0IHRleHQtYW5jaG9yPSJtaWRkbGUiIHg9IjE1MCIgeT0iMTAwIiBmaWxsPSIjOTk5Ij5JbWFnZSBub3QgYXZhaWxhYmxlPC90ZXh0Pjwvc3ZnPg==";
                        img.alt = "Image not available";
                      }}
                    />
                    {editMode && (
                      <button
                        className="remove-picture-overlay"
                        onClick={() => handleRemovePicture(index)}
                        title="Remove picture"
                      >
                        ×
                      </button>
                    )}
                  </div>
                  <div className="picture-description">
                    <span className="picture-number">Picture {index + 1}</span>
                    {editMode ? (
                      <input
                        type="text"
                        className="description-input"
                        value={pic.description || ""}
                        onChange={(e) =>
                          handleUpdatePictureDescription(
                            index,
                            false,
                            e.target.value
                          )
                        }
                        placeholder="Enter description..."
                      />
                    ) : (
                      <p>{pic.description || "No description provided"}</p>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Show uploading pictures in edit mode */}
            {editMode &&
              uploadingPictures.map((pic, index) => (
                <div key={`new-${index}`} className="picture-card new-picture">
                  <div className="picture-container">
                    <img src={pic.preview} alt={`New picture ${index + 1}`} />
                    <button
                      className="remove-picture-overlay"
                      onClick={() => handleRemoveUploadingPicture(index)}
                      title="Remove picture"
                    >
                      ×
                    </button>
                  </div>
                  <div className="picture-description">
                    <span className="picture-number">New Picture</span>
                    <input
                      type="text"
                      className="description-input"
                      value={pic.description}
                      onChange={(e) =>
                        handleUpdatePictureDescription(
                          index,
                          true,
                          e.target.value
                        )
                      }
                      placeholder="Enter description..."
                    />
                  </div>
                </div>
              ))}
          </div>
        </div>
      ) : null}

      {/* Action Buttons */}
      <div className="report-section">
        <h3>Actions</h3>
        <div className="action-buttons-section">
          {/* Admin can approve reports in review (NO reject button for admin) */}
          {isAdmin && report.status === "review" && !editMode && (
            <>
              <button
                onClick={() => handleStatusChange("approved")}
                className="approve-btn"
              >
                Approve Report
              </button>
              <button
                onClick={() => handleStatusChange("action_required")}
                className="action-required-btn"
                style={{
                  backgroundColor: "#f56565",
                  color: "white",
                  marginLeft: "10px"
                }}
              >
                Action Required
              </button>
            </>
          )}

          {/* Admin can revert approved reports back to review */}
          {canRevert && !editMode && (
            <button onClick={handleRevertToReview} className="revert-btn">
              Revert to Review
            </button>
          )}


          {report.status !== "review" && !canRevert && !isSupporter && (
            <div className="status-display">Report is {report.status.replace('_', ' ')}</div>
          )}
        </div>
      </div>


      {/* Footer with Back button */}
      <div className="report-footer">
        <button onClick={() => navigate("/reports")} className="back-btn">
          ← Back to Reports
        </button>
      </div>
    </div>
  );
}

export default ReportDetail;
